import React, { useEffect, useState } from "react";
import { Arrow } from "./other/arrow";
import { handleTaskBySVGMouseEvent } from "./helpers/bar-helper";
import { isKeyboardEvent } from "./helpers/other-helper";
import { TaskItem } from "./task-item/task-item";

export const TaskGanttContent = ({
  tasks,
  dates,
  ganttEvent,
  selectedTask,
  rowHeight,
  columnWidth,
  timeStep,
  svg,
  taskHeight,
  arrowColor,
  arrowIndent,
  fontFamily,
  fontSize,
  rtl,
  setGanttEvent,
  setFailedTask,
  setSelectedTask,
  onDateChange,
  onProgressChange,
  onDoubleClick,
  onClick,
  onDelete
}) => {
  const point = svg?.current?.createSVGPoint();
  const [xStep, setXStep] = useState(0);
  const [initEventX1Delta, setInitEventX1Delta] = useState(0);
  const [isMoving, setIsMoving] = useState(false);

  // calculate xStep (distance of one date change in px)
  useEffect(() => {
    const dateDelta =
      dates[1].getTime() -
      dates[0].getTime() -
      dates[1].getTimezoneOffset() * 60 * 1000 +
      dates[0].getTimezoneOffset() * 60 * 1000;

    const newXStep = (timeStep * columnWidth) / dateDelta;
    setXStep(newXStep);
  }, [columnWidth, dates, timeStep]);

  useEffect(() => {
    const handleMouseMove = async(event) => {
      if (!ganttEvent.changedTask || !point || !svg?.current) return;
      event.preventDefault();

      point.x = event.clientX;
      const cursor = point.matrixTransform(
        svg.current.getScreenCTM()?.inverse()
      );

      const { isChanged, changedTask } = handleTaskBySVGMouseEvent(
        cursor.x,
        ganttEvent.action,
        ganttEvent.changedTask,
        xStep,
        timeStep,
        initEventX1Delta,
        rtl
      );

      if (isChanged) {
        setGanttEvent({ action: ganttEvent.action, changedTask });
      }
    };

    const handleMouseUp = async(event) => {
      const { action, originalSelectedTask, changedTask } = ganttEvent;
      if (!changedTask || !point || !svg?.current || !originalSelectedTask)
        return;

      event.preventDefault();
      point.x = event.clientX;

      const cursor = point.matrixTransform(
        svg.current.getScreenCTM()?.inverse()
      );

      const { changedTask: newChangedTask } = handleTaskBySVGMouseEvent(
        cursor.x,
        action,
        changedTask,
        xStep,
        timeStep,
        initEventX1Delta,
        rtl
      );

      const isNotLikeOriginal =
        originalSelectedTask.start !== newChangedTask.start ||
        originalSelectedTask.end !== newChangedTask.end ||
        originalSelectedTask.progress !== newChangedTask.progress;

      // stop listeners
      svg.current.removeEventListener("mousemove", handleMouseMove);
      svg.current.removeEventListener("mouseup", handleMouseUp);
      setGanttEvent({ action: "" });
      setIsMoving(false);

      // callbacks
      let operationSuccess = true;

      if (
        (action === "move" || action === "end" || action === "start") &&
        onDateChange &&
        isNotLikeOriginal
      ) {
        try {
          const result = await onDateChange(
            newChangedTask,
            newChangedTask.barChildren
          );
          if (result !== undefined) operationSuccess = result;
        } catch (e) {
          operationSuccess = false;
        }
      } else if (onProgressChange && isNotLikeOriginal) {
        try {
          const result = await onProgressChange(
            newChangedTask,
            newChangedTask.barChildren
          );
          if (result !== undefined) operationSuccess = result;
        } catch (e) {
          operationSuccess = false;
        }
      }

      if (!operationSuccess) {
        setFailedTask(originalSelectedTask);
      }
    };

    if (
      !isMoving &&
      ["move", "end", "start", "progress"].includes(ganttEvent.action) &&
      svg?.current
    ) {
      svg.current.addEventListener("mousemove", handleMouseMove);
      svg.current.addEventListener("mouseup", handleMouseUp);
      setIsMoving(true);
    }
  }, [
    ganttEvent,
    xStep,
    initEventX1Delta,
    onProgressChange,
    timeStep,
    onDateChange,
    svg,
    isMoving,
    point,
    rtl,
    setFailedTask,
    setGanttEvent
  ]);

  /** Start drag/change/select events */
  const handleBarEventStart = async(
    action,
    task,
    event
  ) => {
    if (!event) {
      if (action === "select") {
        setSelectedTask(task.id);
      }
      return;
    }

    // keyboard events
    if (isKeyboardEvent(event)) {
      if (action === "delete" && onDelete) {
        try {
          const result = await onDelete(task);
          if (result) {
            setGanttEvent({ action, changedTask: task });
          }
        } catch (e) {
          console.error("Error on delete", e);
        }
      }
      return;
    }

    // mouse events
    if (action === "mouseenter") {
      if (!ganttEvent.action) {
        setGanttEvent({
          action,
          changedTask: task,
          originalSelectedTask: task
        });
      }
    } else if (action === "mouseleave") {
      if (ganttEvent.action === "mouseenter") {
        setGanttEvent({ action: "" });
      }
    } else if (action === "dblclick") {
      onDoubleClick && onDoubleClick(task);
    } else if (action === "click") {
      onClick && onClick(task);
    } else if (action === "move") {
      if (!svg?.current || !point) return;

      point.x = event.clientX;
      const cursor = point.matrixTransform(
        svg.current.getScreenCTM()?.inverse()
      );

      setInitEventX1Delta(cursor.x - task.x1);
      setGanttEvent({
        action,
        changedTask: task,
        originalSelectedTask: task
      });
    } else {
      setGanttEvent({
        action,
        changedTask: task,
        originalSelectedTask: task
      });
    }
  };

  return (
    <g className="content">
      {/* arrows */}
      <g className="arrows" fill={arrowColor} stroke={arrowColor}>
        {tasks.map(task =>
          task.barChildren.map(child => (
            <Arrow
              key={`Arrow from ${task.id} to ${tasks[child.index].id}`}
              taskFrom={task}
              taskTo={tasks[child.index]}
              rowHeight={rowHeight}
              taskHeight={taskHeight}
              arrowIndent={arrowIndent}
              rtl={rtl}
            />
          ))
        )}
      </g>

      {/* bars */}
      <g className="bar" fontFamily={fontFamily} fontSize={fontSize}>
        {tasks.map(task => (
          <TaskItem
            key={task.id}
            task={task}
            arrowIndent={arrowIndent}
            taskHeight={taskHeight}
            isProgressChangeable={!!onProgressChange && !task.isDisabled}
            isDateChangeable={!!onDateChange && !task.isDisabled}
            isDelete={!task.isDisabled}
            isSelected={!!selectedTask && selectedTask.id === task.id}
            onEventStart={handleBarEventStart}
            rtl={rtl}
          />
        ))}
      </g>
    </g>
  );
};
